import React from "react";

import ExclamationMarkIcon from "@src/assets/icons/exclamation-mark.svg";
import { CardLabel } from "@src/components/card_label/index";
import { RequestOverviewDetails } from "@src/types";
import { formatDate } from "@src/utils/helper";
import { Card, Flex, Col, Row } from "antd";
import _ from "lodash";

interface StaffingRequestInformationCardProps {
  request: RequestOverviewDetails;
}

type FreeTextField = {
  label: string;
  fieldKey: string;
  value: string;
};

const StaffingRequestInformationCard: React.FC<
  StaffingRequestInformationCardProps
> = ({ request }) => {
  const scopeDescription: string =
    request?.childRequest?.scopeDescription ?? request?.scopeDescription;
  const specificRole: string =
    request?.childRequest?.specificRole ?? request?.specificRole;
  const projectIdentifier: string =
    request?.childRequest?.projectIdentifier ?? request?.projectIdentifier;

  const freeTextFields: FreeTextField[] = [
    {
      label: "Scope Description",
      fieldKey: "scopeDescription",
      value: scopeDescription,
    },
    {
      label: "Project Identifier",
      fieldKey: "projectIdentifier",
      value: projectIdentifier,
    },
    {
      label: "Specific Role",
      fieldKey: "specificRole",
      value: specificRole,
    },
  ];

  /**
   * Checks if a free text field has changed between the original request and the child request
   * @param type - The type of free text field to check (scopeDescription, specificRole, projectIdentifier)
   */
  function hasFreeTextFieldChanged(type: string) {
    if (!request?.childRequest) return false;

    return request.childRequest?.[type] !== request?.[type];
  }

  /**
   * Generates the tooltip text for a free text field based on its change status.
   * @param field - The free text field object containing label, fieldKey, and value.
   */
  function getToolTipText(field: FreeTextField) {
    if (!request?.childRequest?.[field.fieldKey] && request?.[field.fieldKey]) {
      return `Removed ${field.label} with the value "${
        request?.[field.fieldKey]
      }"`;
    }

    if (request?.[field.fieldKey]) {
      return `The initial value "${
        request?.[field.fieldKey]
      }" was changed to "${request?.childRequest?.[field.fieldKey]}"`;
    } else if (request?.childRequest?.[field.fieldKey]) {
      return `Added initial ${field.label} with the value "${
        request?.childRequest?.[field.fieldKey]
      }"`;
    }
  }

  return (
    <Card
      title="Request"
      className="info-card"
      data-testid="request-information-card"
    >
      <Row gutter={[10, 0]}>
        {/*left side*/}
        <Col span={12}>
          <Flex vertical gap={10}>
            <CardLabel label="Initially Sent">
              {formatDate(request.requestSentDate)}
            </CardLabel>
            <CardLabel label="By">{request.sendBy?.fullName}</CardLabel>
            {request.isHandedOver && request.handedOverBy && (
              <CardLabel label="Forwarded by">
                {request.handedOverBy.fullName}
              </CardLabel>
            )}
          </Flex>
        </Col>

        {/*right side*/}
        <Col span={12}>
          <Flex vertical gap={10}>
            {freeTextFields.map((field) => (
              <CardLabel
                key={field.fieldKey}
                label={field.label}
                showToolTip={hasFreeTextFieldChanged(field.fieldKey)}
                toolTip={getToolTipText(field)}
              >
                {hasFreeTextFieldChanged(field.fieldKey) ? (
                  <span
                    className="project-information-card__exclamation-icon"
                    data-testid={`exclamation-icon-${field.fieldKey}`}
                  >
                    <ExclamationMarkIcon />
                  </span>
                ) : null}

                <span
                  data-testid={`project_information_card__${_.kebabCase(
                    field.fieldKey
                  )}-label`}
                >
                  {field.value}
                </span>
              </CardLabel>
            ))}
          </Flex>
        </Col>
      </Row>
    </Card>
  );
};

export default StaffingRequestInformationCard;
